import { useSendMetricsOfProduct } from '@/composables/useProductMetrics'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useBasketChangeStore } from '@/store/basket/basket-change'

import { Product } from '@/types/api/api-v3/product'
import type { NewProduct } from '@/types/api/api-v4/product'
import type { ProductV5 } from '@/types/api/api-v5/product'
import { EcommerceEventName } from '@/types/plugins/yandex-metrika'
import type { CommonProductModification } from '@/types/products'

import { debounce } from 'throttle-debounce'

export const useProductCounter = (
  productId: ComputedRef<number>,
  currentProduct?: ComputedRef<CommonProductModification | null>,
  product?: Product | NewProduct | ProductV5,
) => {
  const previousQuantity = ref(0)
  // Счетчик кликов без учета единиц продуктов в корзине
  const countClickedQuantity = ref(0)

  const appStore = useAppStore()
  const basketStore = useBasketStore()
  const basketChangeStore = useBasketChangeStore()

  const basketError = computed(() => basketStore.error)

  const quantitySelectedProducts = computed(() => {
    return basketChangeStore.getQuantitySelectedProducts(productId.value)
  })

  // Получаем общее количество модификаций в корзине в рамках одного товара
  const totalSelectedProductsQuantity = computed(() => {
    if (!product) return 0

    let totalCount = 0

    for (const { id } of product.modifications) {
      totalCount += basketChangeStore.getQuantitySelectedProducts(id)
    }

    return totalCount
  })

  const { pushMetrics } = useSendMetricsOfProduct(currentProduct?.value)

  const setProductQuantity = (quantity: number) => {
    basketChangeStore.setQuantitySelectedProducts({
      index: productId.value,
      value: quantity,
    })
  }

  // Устанавливаем общее количество модификаций добавленных в корзину в рамках одного товара
  const setTotalProductsQuantity = () => {
    if (!product) return

    for (const { id } of product.modifications) {
      const value = basketStore.basketSelectedProducts[id]

      if (value === undefined) continue

      basketChangeStore.setQuantitySelectedProducts({
        index: id,
        value,
      })
    }
  }

  const changeQuantity = debounce(
    300,
    async (
      quantity: number,
      options: {
        eventType?: EcommerceEventName
      },
    ) => {
      await basketStore.setProduct({
        product_id: productId.value,
        productPrice: Number(currentProduct?.value?.price),
        quantity,
      })

      // Отправляем метрику добавления/удаляения единиц продукта
      if (countClickedQuantity.value && options.eventType)
        pushMetrics(options.eventType, countClickedQuantity.value)

      // Сбрасываем счетчик кликов
      countClickedQuantity.value = 0

      // Если произошла ошибка, приводим кол- во товара к изначальному и выводим снекбар
      if (basketError.value) {
        setProductQuantity(previousQuantity.value)

        // Показываем снекбар с ошибкой
        appStore.showSnackbar({
          message: 'Что-то пошло не так',
          type: SnackbarType.MESSAGE,
        })
      }
    },
  )

  const decreaseQuantity = () => {
    if (quantitySelectedProducts.value === 0) return

    previousQuantity.value = quantitySelectedProducts.value

    const newQuantity = quantitySelectedProducts.value - 1

    countClickedQuantity.value += 1

    setProductQuantity(newQuantity)
    changeQuantity(newQuantity, { eventType: EcommerceEventName.REMOVE })
  }

  const increaseQuantity = () => {
    previousQuantity.value = quantitySelectedProducts.value

    const newQuantity = quantitySelectedProducts.value + 1

    // Фиксируем кол-во кликов
    countClickedQuantity.value += 1

    setProductQuantity(newQuantity)
    changeQuantity(newQuantity, { eventType: EcommerceEventName.ADD })
  }

  return {
    basketError,
    quantitySelectedProducts,
    totalSelectedProductsQuantity,
    setProductQuantity,
    setTotalProductsQuantity,
    decreaseQuantity,
    increaseQuantity,
    changeQuantity,
  }
}
